import { useState, type FormEvent, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ASSETS } from '../../lib/brand';
import { useAuthStore } from '../../store/authStore';
import { APP_NAME, APP_VERSION, BUILT_BY, COMPANY_DISPLAY_NAME, COPYRIGHT_YEAR } from '../../lib/appInfo';

function AuthCard({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        bgcolor: 'background.default',
      }}
    >
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 400, p: { xs: 3, sm: 4 } }}>
        <Stack spacing={1} sx={{ alignItems: 'center', mb: 3, textAlign: 'center' }}>
          <Box component="img" src={ASSETS.logo} alt="AST" sx={{ height: 40, mb: 1 }} />
          <Typography variant="h6" component="h1">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Stack>
        {children}
      </Paper>
      <Typography variant="caption" component="footer" sx={{ textAlign: 'center', mt: 3 }}>
        © {COPYRIGHT_YEAR} {COMPANY_DISPLAY_NAME}
        <br />
        {APP_NAME} v{APP_VERSION} by {BUILT_BY}
      </Typography>
    </Box>
  );
}

export function LoginScreen() {
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Sign in to Slide" subtitle="Use the account your admin created for you.">
      <Stack component="form" spacing={2} onSubmit={submit} noValidate>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          autoFocus
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={busy || !email || !password}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        <Typography variant="caption" sx={{ textAlign: 'center' }}>
          Forgot your password? Ask an admin to reset it.
        </Typography>
      </Stack>
    </AuthCard>
  );
}

/** Current + new password form; `onDone` runs after a successful change. */
function PasswordForm({ onDone, submitLabel }: { onDone?: () => void; submitLabel: string }) {
  const changePassword = useAuthStore((s) => s.changePassword);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mismatch = confirm.length > 0 && confirm !== next;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== confirm) return;
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      onDone?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack component="form" id="password-form" spacing={2} onSubmit={submit} noValidate>
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="Current password"
        type="password"
        autoComplete="current-password"
        fullWidth
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
      />
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        fullWidth
        helperText="At least 10 characters, with letters and a number"
        value={next}
        onChange={(e) => setNext(e.target.value)}
      />
      <TextField
        label="Confirm new password"
        type="password"
        autoComplete="new-password"
        fullWidth
        error={mismatch}
        helperText={mismatch ? "Passwords don't match" : ' '}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />
      <Button
        type="submit"
        variant="contained"
        disabled={busy || !current || !next || next !== confirm}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {submitLabel}
      </Button>
    </Stack>
  );
}

/** Shown after signing in with a temporary password; the editor stays locked until it's changed. */
export function ForcedPasswordScreen() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  return (
    <AuthCard
      title="Set your password"
      subtitle={`You signed in as ${user?.email} with a temporary password. Choose your own to continue.`}
    >
      <PasswordForm submitLabel="Save and continue" />
      <Button color="neutral" fullWidth sx={{ mt: 1 }} onClick={() => void logout()}>
        Sign out
      </Button>
    </AuthCard>
  );
}

export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [done, setDone] = useState(false);
  const close = () => {
    setDone(false);
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby="change-password-title">
      <DialogTitle id="change-password-title" component="h2" variant="h6">
        Change password
      </DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success">Password changed. Other devices have been signed out.</Alert>
        ) : (
          <PasswordForm submitLabel="Change password" onDone={() => setDone(true)} />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="neutral" onClick={close}>
          {done ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
